/* eslint-disable max-lines-per-function */
/* eslint-disable prefer-destructuring */

"use client"

import { usePathname, useSearchParams } from "next/navigation"
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useReducer
} from "react"

import { parseCasinoPathname } from "@/utils/casino-lobby-path"

import { FilterState } from "../interfaces/filter.interface"
import { GameQueryParams } from "../interfaces/query.interface"
import { casinoReducer, initialState } from "./casinoReducer"
import { useCasinoGames } from "./useCasinoGames"

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export function useCasinoLobby() {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const pathLobby = useMemo(() => parseCasinoPathname(pathname), [pathname])
  const loadGamesRef = useRef<(() => void) | undefined>(undefined)

  const [state, dispatch] = useReducer(casinoReducer, initialState)
  const { fetchGames, lastFetchParamsRef } = useCasinoGames(dispatch)

  const {
    games,
    loading,
    error,
    filters,
    search,
    selectedView,
    sortBy,
    pagination
  } = state

  const debouncedSearchQuery = useDebounce(search.query, 300)

  const parseCommaSeparatedValues = useCallback(
    (value: string | null): string[] => {
      if (!value) {
        return []
      }
      return value.includes(",") ? value.split(",").filter(Boolean) : [value]
    },
    []
  )

  const hasUrlLobbyParams = Boolean(
    pathLobby.hasFilter ||
    searchParams.get("view") ||
    searchParams.get("category") ||
    searchParams.get("categories") ||
    searchParams.get("provider") ||
    searchParams.get("collection") ||
    searchParams.get("search")
  )

  const buildQueryParamsHelper = useCallback(
    (
      baseParams: Partial<GameQueryParams>,
      searchQuery?: string
    ): GameQueryParams => {
      const params: GameQueryParams = {
        quantity: 50,
        offset: 0,
        sortBy,
        ...baseParams
      }

      const categories =
        filters.categories.length > 0
          ? filters.categories
          : pathLobby.hasFilter
            ? pathLobby.categories
            : parseCommaSeparatedValues(
                searchParams.get("category") || searchParams.get("categories")
              )

      if (categories.length === 1) {
        params.category = categories[0]
      } else if (categories.length > 1) {
        params.categories = categories.join(",")
      }

      const providers =
        filters.providers.length > 0
          ? filters.providers
          : pathLobby.producer
            ? [pathLobby.producer]
            : []

      if (providers.length === 1) {
        params.provider = providers[0]
      } else if (providers.length > 1) {
        params.providers = providers.join(",")
      }

      if (filters.collections.length > 0) {
        if (filters.collections.length === 1) {
          params.collection = filters.collections[0]
        } else {
          params.collections = filters.collections.join(",")
        }
      }

      if (searchQuery) {
        params.search = searchQuery
      }

      return params
    },
    [filters, sortBy, searchParams, parseCommaSeparatedValues, pathLobby]
  )

  const buildQueryParams = useCallback((): GameQueryParams => {
    return buildQueryParamsHelper(
      { offset: pagination.currentPage * 50 },
      debouncedSearchQuery
    )
  }, [buildQueryParamsHelper, pagination.currentPage, debouncedSearchQuery])

  const searchGames = useCallback(async (query: string) => {
    if (query.length < 2) {
      dispatch({ type: "SET_SUGGESTIONS", payload: [] })
      return
    }

    try {
      dispatch({ type: "SET_SEARCHING", payload: true })
      const response = await fetch("/api/casino/games", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ searchQuery: query, limit: 10 })
      })

      if (response.ok) {
        const data = await response.json()
        dispatch({ type: "SET_SUGGESTIONS", payload: data.suggestions })
      }
    } catch (err) {
      console.warn("Search error:", err)
    } finally {
      dispatch({ type: "SET_SEARCHING", payload: false })
    }
  }, [])

  const loadMore = useCallback(() => {
    if (loading || !pagination.hasMore) {
      return
    }

    const nextPage = pagination.currentPage + 1
    dispatch({ type: "SET_PAGINATION", payload: { currentPage: nextPage } })

    const params = buildQueryParamsHelper(
      { offset: nextPage * 50 },
      search.query
    )

    fetchGames(false, params)
  }, [
    fetchGames,
    loading,
    pagination.hasMore,
    pagination.currentPage,
    buildQueryParamsHelper,
    search.query
  ])

  const reset = useCallback(() => {
    dispatch({ type: "RESET_ALL" })
  }, [])

  const isLoadingRef = useRef(false)

  const loadGames = useCallback(() => {
    if (
      isLoadingRef.current ||
      (selectedView !== "games" && !hasUrlLobbyParams)
    ) {
      return
    }

    isLoadingRef.current = true

    const specificParams = buildQueryParamsHelper({ offset: 0 }, search.query)

    const currentParamsKey = JSON.stringify(specificParams)

    if (currentParamsKey !== lastFetchParamsRef.current) {
      lastFetchParamsRef.current = currentParamsKey
      fetchGames(true, specificParams).finally(() => {
        isLoadingRef.current = false
      })
    } else {
      isLoadingRef.current = false
    }
  }, [
    fetchGames,
    selectedView,
    hasUrlLobbyParams,
    buildQueryParamsHelper,
    search.query,
    lastFetchParamsRef
  ])

  const applyFilters = useCallback(() => {
    dispatch({ type: "CLEAR_GAMES" })
    dispatch({ type: "SET_PAGINATION", payload: { currentPage: 0 } })
    const params = buildQueryParams()
    fetchGames(true, params)
  }, [fetchGames, buildQueryParams])

  loadGamesRef.current = loadGames

  useEffect(() => {
    if (debouncedSearchQuery) {
      searchGames(debouncedSearchQuery)
    } else {
      dispatch({ type: "SET_SUGGESTIONS", payload: [] })
    }
  }, [debouncedSearchQuery, searchGames])

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (selectedView === "games" || hasUrlLobbyParams) {
      const timer = setTimeout(() => {
        if (loadGamesRef.current) {
          loadGamesRef.current()
        }
      }, 100)

      return () => {
        clearTimeout(timer)
      }
    }
  }, [selectedView, hasUrlLobbyParams, filters, sortBy])

  useEffect(() => {
    if (selectedView === "games" && debouncedSearchQuery) {
      const timer = setTimeout(() => {
        if (loadGamesRef.current) {
          loadGamesRef.current()
        }
      }, 100)

      return () => {
        clearTimeout(timer)
      }
    }
    return undefined
  }, [selectedView, debouncedSearchQuery])

  const parseUrlParams = useCallback(() => {
    if (pathLobby.hasFilter) {
      dispatch({ type: "SET_SELECTED_VIEW", payload: "games" })
      dispatch({
        type: "SET_FILTERS",
        payload: {
          categories: pathLobby.categories,
          providers: pathLobby.producer ? [pathLobby.producer] : [],
          collections: []
        }
      })
      return
    }

    const urlParams = {
      category: searchParams.get("category"),
      categories: searchParams.get("categories"),
      provider: searchParams.get("provider"),
      providers: searchParams.get("providers"),
      collection: searchParams.get("collection"),
      collections: searchParams.get("collections"),
      view: searchParams.get("view"),
      searchQuery: searchParams.get("search")
    }

    const hasAnyParam = Object.values(urlParams).some(Boolean)
    if (hasAnyParam) {
      dispatch({ type: "SET_SELECTED_VIEW", payload: "games" })
    }

    const newFilters = {
      categories: parseCommaSeparatedValues(
        urlParams.category || urlParams.categories
      ),
      providers: parseCommaSeparatedValues(
        urlParams.provider || urlParams.providers
      ),
      collections: parseCommaSeparatedValues(
        urlParams.collection || urlParams.collections
      )
    }

    dispatch({ type: "SET_FILTERS", payload: newFilters })

    if (urlParams.searchQuery) {
      dispatch({ type: "SET_SEARCH_QUERY", payload: urlParams.searchQuery })
    }
  }, [searchParams, parseCommaSeparatedValues, pathLobby])

  useEffect(() => {
    parseUrlParams()
  }, [parseUrlParams])

  useEffect(() => {
    const searchQuery = searchParams.get("search")
    if (searchQuery && searchQuery !== state.search.query) {
      dispatch({ type: "SET_SEARCH_QUERY", payload: searchQuery })
    }
  }, [searchParams, state.search.query])

  const computedValues = useMemo(() => {
    return {
      isEmpty: games.length === 0 && !loading,
      canLoadMore: pagination.hasMore && !loading,
      isSearching: search.isSearching,
      hasActiveFilters:
        filters.categories.length > 0 ||
        filters.providers.length > 0 ||
        filters.collections.length > 0,
      hasSearchQuery: search.query.length > 0
    }
  }, [
    games.length,
    loading,
    pagination.hasMore,
    search.isSearching,
    search.query,
    filters
  ])

  return {
    // State
    games,
    loading,
    error,
    hasMore: pagination.hasMore,
    totalCount: pagination.totalCount,
    filters,
    search,
    selectedView,
    sortBy,
    ...computedValues,

    // Actions
    setSearchQuery: (query: string) => {
      return dispatch({ type: "SET_SEARCH_QUERY", payload: query })
    },
    clearSearch: () => {
      return dispatch({ type: "CLEAR_SEARCH" })
    },
    setSelectedView: (view: "lobby" | "games") => {
      return dispatch({ type: "SET_SELECTED_VIEW", payload: view })
    },
    setSortBy: (sort: "newest" | "popular" | "alphabetical") => {
      return dispatch({ type: "SET_SORT_BY", payload: sort })
    },
    loadMore,
    loadGames,
    applyFilters,
    reset,
    fetchGames,

    // Filters actions
    setFilters: (newFilters: Partial<FilterState>) => {
      return dispatch({ type: "SET_FILTERS", payload: newFilters })
    },
    clearFilters: () => {
      return dispatch({ type: "CLEAR_FILTERS" })
    },
    toggleFilter: (type: keyof FilterState, value: string) => {
      return dispatch({ type: "TOGGLE_FILTER", payload: { type, value } })
    }
  }
}

export function useCasinoSearch() {
  const { search, setSearchQuery, clearSearch } = useCasinoLobby()

  const debouncedQuery = useDebounce(search.query, 300)

  return {
    query: search.query,
    suggestions: search.suggestions,
    isSearching: search.isSearching,
    setQuery: setSearchQuery,
    clear: clearSearch,
    debouncedQuery
  }
}
